<template>
  <main>
    <Trail
      :list="[
        {
          path: '/proyectos/investigacion',
          name: 'Projects',
          tag: 'Proyectos de Investigación'
        },
        {
          name: 'VOCALS',
          tag: 'VOCALS'
        }
      ]"
    />
    <Container class="container--main">
      <Title tag="h1" align="center">
        VAMOS Ocean-Cloud-Land-Atmosphere Study (VOCALS)
      </Title>
      <Row>
        <ColumnUi md="9">
          <Navtab id="introduccion">
            <Title tag="h2">Introducción</Title>
            <p>
              El proyecto
              <a href="http://www.eol.ucar.edu/projects/vocals" target="_blank">
                VOCALS
              </a>
              es un esfuerzo internacional cuyo principal objetivo es mejorar el
              entendimiento de los factores que afectan el clima en el oceano
              Pacífico suroriental, frente a la costa de Sudamérica. Este
              interés está fuertemente motivado por la gran influencia que
              tienen las persistentes nubes bajas en esta región sobre el
              <a
                href="http://met.igp.gob.pe/proyectos/vocals/cc_conceptos.html#balance"
                target="_blank"
                >balance de energía</a
              >
              del planeta, las cuales son además
              <a
                href="http://www.agu.org/pubs/crossref/2005.../2005GL023851.shtml"
                target="_blank"
              >
                una de las principales fuentes de incertidumbre</a
              >
              en los escenarios climáticos futuros.
            </p>

            <p>
              Para Perú, esta región tiene gran importancia a escala más local,
              ya que el mar peruano tiene una gran riqueza biológica que nos
              hace uno de los principales productores pesqueros del mundo. Esto
              se debe principalmente a la acción del viento a lo largo de la
              costa y del efecto de la rotación de la Tierra, el cual resulta en
              un
              <a
                href="http://www.peruecologico.com.pe/lib_c4_t06.htm"
                target="_blank"
              >
                afloramiento costero
              </a>
              de aguas más profundas que contienen nutrientes que favorecen la
              producción biológica.
            </p>
            <p>
              La participación del Perú en el proyecto VOCALS ha estado
              orientada a un mejor entendimiento de los procesos relevantes al
              ecosistema del mar peruano. En particular, el Instituto Geofísico
              del Perú (IGP) se ha concentrado en entender los aspectos
              meteorológicos cerca a la costa utilizando una combinación de
              observaciones, teoría y modelos. Las preguntas científicas
              principales que se buscan responder son:
            </p>
            <List tag="ol">
              <li>
                ¿Cuál es la distribución espacial de los vientos cerca a la
                costa y cuáles son los procesos físicos responsables?
              </li>
              <li>
                ¿Por qué la región de Ica tiene cielos persistentemente
                despejados, en contraste con el resto de la costa?
              </li>
            </List>
          </Navtab>
          <Navtab id="vocals-rex">
            <Title tag="h2">
              VOCALS-REx
            </Title>
            <p>
              Una de las principales actividades dentro de VOCALS fue el
              <a
                href="http://www.atmos.washington.edu/~robwood/VOCALS/vocals_uw.html"
              >
                "experimento regional" (VOCALS REx)</a
              >, el cual consistió en una campaña observacional intensiva que
              tuvo lugar entre octubre y noviembre del 2008 y que contó con una
              gran variedad de plataformas e instrumentos <b>(Ver Figura 1)</b>.
            </p>
            <Figure maxWidth="600px">
              <Image
                linked="http://www.atmos.washington.edu/~robwood/VOCALS/vocals_uw.html"
                :srcsets="['/images/proyectos/vocals/vocals_rex.jpg']"
                title="Experimento regional (VOCALS REx)"
              />
              <template #figcaption>
                <b>Figura 1</b>. VOCALS at the University of Washington
              </template>
            </Figure>
            <p>
              Como parte de esta campaña, en octubre del 2008 el Perú realizó un
              <a
                href="http://met.igp.gob.pe/proyectos/vocals_rex"
                target="_blank"
              >
                crucero oceanográfico
              </a>
              de dos semanas frente a la costa de Ica, la cual es una de las
              zonas de vientos más intensos y mayor
              <a
                href="http://www.peruecologico.com.pe/lib_c4_t06.htm"
                target="_blank"
              >
                afloramiento costero</a
              >, en colaboración con el
              <a href="http://www.imarpe.gob.pe" target="_blank">
                Instituto del Mar del Perú (IMARPE)
              </a>
              y el
              <a href="http://www.ird.fr" target="_blank">
                Institut de Recherche pour le Développement (IRD)
              </a>
              de Francia. Para el crucero se utilizó el
              <a
                href="http://www.imarpe.pe/imarpe/index.php?id_seccion=I0114020000000000000000"
                target="_blank"
              >
                BIP José Olaya
              </a>
              del IMARPE. El IGP estuvo a cargo de la componente atmosférica de
              estas observaciones, la cual consistió en el lanzamiento de 132
              <a
                href="http://www.eol.ucar.edu/instrumentation/sounding/gaus"
                target="_blank"
              >
                radiosondas
              </a>
              (<b>ver Figura 2</b>). Los datos de los radiosondeos se encuentran
              en la fase final del
              <a
                href="http://met.igp.gob.pe/proyectos/vocals_rex/atmos"
                target="_blank"
              >
                control de calidad y de corrección de errores</a
              >.
            </p>
            <Figure maxWidth="600px">
              <Image
                :srcsets="['/images/proyectos/vocals/RSlocations.gif']"
                title="Ubicación de los radiosondeos junto con vientos superficiales estimados por satélite y la elevación del terreno"
              />
              <template #figcaption>
                <b>Figura 2</b>. Ubicación de los radiosondeos junto con vientos
                superficiales estimados por satélite y la elevación del terreno
              </template>
            </Figure>
          </Navtab>
          <Navtab id="analisis-modelado">
            <Title tag="h2">
              Análisis y modelado numérico
            </Title>
            <p>
              El análisis de los datos se centrará en la identificación de una
              intensificación de los vientos cerca a la costa (jet costero) y en
              contrastar la estructura termodinámica de la atmósfera asociada a
              los cielos despejados y nublados.
            </p>
            <p>
              Debido a que las observaciones se hicieron en distintos lugares y
              tiempo, no son suficientes por si solos para determinar la
              estructura tridimensional promedio de la circulación y
              termodinámica. Por esto, desde un inicio se planificó el estudio
              como una actividad que combinara el trabajo observacional con
              modelado numérico.
            </p>
            <p>
              Una ventaja del modelado numérico es que permite acceso total a
              las diferentes variables de interés en todo lugar y todo momento,
              a diferencia de las observaciones. Por otro lado, los modelos
              pueden tener
              <a
                href="http://met.igp.gob.pe/modelos/modelos.html#errores"
                target="_blank"
              >
                errores
              </a>
              y no se pueden confiar a menos de que se haya realizado una
              <a
                href="http://met.igp.gob.pe/modelos/modelos.html#errores"
                target="_blank"
              >
                validación
              </a>
              previamente. Para la validación es crucial tener información
              observacional.
            </p>
            <p>
              En resumen, las observaciones servirán para validar el modelado, y
              el modelado servirá para poner las observaciones en contexto.
            </p>
            <p>
              Una vez que se valida el modelo exitosamente, se puede utilizar
              para realizar
              <a
                href="http://met.igp.gob.pe/modelos/experimentos.html"
                target="_blank"
              >
                experimentos numéricos
              </a>
              que permitan probar teorías sobre los procesos físicos importantes
              para la circulación y termodinámica de la atmósfera costera.
            </p>
            <p>
              Las teorías verificadas constituirán un nuevo cuerpo de
              conocimientos que se podrán utilizar para otras aplicaciones, como
              el desarrollo de modelos de pronóstico o
              <a
                href="http://met.igp.gob.pe/proyectos/modelos/escenarios.html"
                target="_blank"
                >generación de escenarios de cambio climático</a
              >.
            </p>
          </Navtab>
        </ColumnUi>
        <ColumnUi md="3">
          <Nav :items="navItems" title="Contenido" />
        </ColumnUi>
      </Row>
    </Container>
  </main>
</template>
<script>
import Trail from "@/components/common/Trail";
import Title from "@/components/ui/atoms/Title";
import List from "@/components/ui/objects/List";
import Container from "@/components/ui/objects/Container";
import Figure from "@/components/ui/objects/Figure";
import Image from "@/components/ui/objects/Image";
import Nav from "@/components/ui/objects/Nav";
import Navtab from "@/components/ui/objects/Navtab";
import Row from "@/components/ui/atoms/Row";
import ColumnUi from "@/components/ui/atoms/ColumnUi";
export default {
  components: {
    Trail,
    Title,
    List,
    Container,
    Figure,
    Image,
    Nav,
    Navtab,
    Row,
    ColumnUi
  },
  setup() {
    const navItems = [
      {
        path: "#introduccion",
        tag: "Introducción"
      },
      {
        path: "#vocals-rex",
        tag: "VOCALS-REx"
      },
      {
        path: "#analisis-modelado",
        tag: "Análisis y modelado numérico"
      }
    ];
    return {
      navItems
    };
  }
};
</script>
